import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ExternalLink, MessageSquareQuote, FileSearch, LayoutDashboard, Kanban, Inbox as InboxIcon, Flame, BookOpen, Radar, Send } from "lucide-react";

const Tile = ({ to, icon: Icon, title, blurb, testId }) => (
  <Link to={to} data-testid={testId}>
    <div className="p-6 rounded-2xl bg-[#141414] border border-white/5 hover:border-[#CCFF00]/40 hover:-translate-y-1 transition-all h-full">
      <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#CCFF00]" />
      </div>
      <h3 className="font-display font-black text-xl mb-1 tracking-tight">{title}</h3>
      <p className="text-sm text-neutral-400">{blurb}</p>
    </div>
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [scans, setScans] = useState([]);
  const [inbox, setInbox] = useState({ items: [], unread: 0 });
  const [roastOpen, setRoastOpen] = useState(false);
  const [roast, setRoast] = useState(null);
  const [roastBusy, setRoastBusy] = useState(false);

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
    api.get("/scan/history").then((r) => setScans(r.data)).catch(() => {});
    api.get("/inbox").then((r) => setInbox(r.data)).catch(() => {});
  }, []);

  const previewRoast = async () => {
    setRoastOpen(true);
    if (roast) return;
    setRoastBusy(true);
    try {
      const r = await api.get("/roast/preview");
      setRoast(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setRoastOpen(false);
    } finally {
      setRoastBusy(false);
    }
  };

  const sendRoast = async () => {
    try {
      const r = await api.post("/roast/send");
      toast.success("Roast sent to your inbox.");
      setRoastOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const totalValue = deals.reduce((a, d) => a + (d.value || 0), 0);
  const activeDeals = deals.filter((d) => !["paid"].includes(d.stage)).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between mb-12 gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">// welcome back</p>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">
            Hey, {user?.display_name?.split(" ")[0] || "creator"}.
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={previewRoast}
            className="rounded-full border border-white/15 hover:bg-white/5"
            data-testid="dashboard-roast-btn"
          >
            <Flame className="w-4 h-4 text-[#CCFF00]" /> Weekly Roast
          </Button>
          <a href={`/${user?.handle}`} target="_blank" rel="noreferrer" data-testid="dashboard-view-mediakit-link">
            <Button variant="ghost" className="rounded-full border border-white/15 hover:bg-white/5">
              View my media kit <ExternalLink className="w-4 h-4 ml-1" />
            </Button>
          </a>
        </div>
      </div>

      <Dialog open={roastOpen} onOpenChange={setRoastOpen}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="roast-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-3xl tracking-tighter flex items-center gap-2">
              <Flame className="w-6 h-6 text-[#CCFF00]" /> This week's roast
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              An AI-generated summary of your DAP week. Preview it, then send it to your inbox.
            </DialogDescription>
          </DialogHeader>
          {roastBusy ? (
            <p className="text-neutral-400 py-8 text-center animate-pulse">Claude is cooking your roast…</p>
          ) : roast ? (
            <div className="space-y-4">
              <p className="text-xs font-mono uppercase tracking-widest text-neutral-500">Subject</p>
              <p className="font-display font-bold text-lg" data-testid="roast-subject">{roast.subject}</p>
              <div
                className="rounded-xl overflow-hidden border border-white/10"
                data-testid="roast-html-preview"
                dangerouslySetInnerHTML={{ __html: roast.html }}
              />
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setRoastOpen(false)}
                  className="rounded-full"
                  data-testid="roast-close-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={sendRoast}
                  className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
                  data-testid="roast-send-btn"
                >
                  Send to my email
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-12" data-testid="dashboard-stats">
        <Stat label="Pitches (unread)" value={inbox.unread} />
        <Stat label="Active deals" value={activeDeals} />
        <Stat label="Pipeline value" value={`$${totalValue.toLocaleString()}`} accent />
        <Stat label="Contract scans" value={scans.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-12">
        <Tile
          to="/app/playbooks"
          icon={BookOpen}
          title="Playbook Library"
          blurb="15 insider playbooks — the growth secrets top creators pay $500 to learn."
          testId="dashboard-tile-playbooks"
        />
        <Tile
          to="/app/radar"
          icon={Radar}
          title="Trend Radar"
          blurb="Weekly AI report: emerging audios, formats, and adjacent niches in your space."
          testId="dashboard-tile-radar"
        />
        <Tile
          to="/app/outbound"
          icon={Send}
          title="Outbound Pitcher"
          blurb="Don't wait to be emailed — draft cold pitches to brands you actually want."
          testId="dashboard-tile-outbound"
        />
        <Tile
          to="/app/inbox"
          icon={InboxIcon}
          title={`Inbox${inbox.unread > 0 ? ` · ${inbox.unread} new` : ""}`}
          blurb="Forward brand pitches to your DAP address. One-click draft reply."
          testId="dashboard-tile-inbox"
        />
        <Tile
          to="/app/copilot"
          icon={MessageSquareQuote}
          title="Deal Copilot"
          blurb="Paste a brand's email → get a warm, confident reply drafted by Claude."
          testId="dashboard-tile-copilot"
        />
        <Tile
          to="/app/pipeline"
          icon={Kanban}
          title="Pipeline"
          blurb="Track every deal from Prospect → Paid. Never lose a lead."
          testId="dashboard-tile-pipeline"
        />
        <Tile
          to="/app/mediakit"
          icon={LayoutDashboard}
          title="Media Kit"
          blurb="Edit your public /@handle page. Big confident numbers."
          testId="dashboard-tile-mediakit"
        />
        <Tile
          to="/scanner"
          icon={FileSearch}
          title="Contract Scanner"
          blurb="Upload a contract PDF, get red flags in seconds."
          testId="dashboard-tile-scanner"
        />
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div
    className={`p-6 rounded-2xl ${accent ? "bg-[#CCFF00] text-black" : "bg-[#141414] border border-white/5"}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <p
      className={`font-mono text-[10px] uppercase tracking-widest ${
        accent ? "text-black/60" : "text-neutral-500"
      }`}
    >
      {label}
    </p>
    <p className="font-display font-black text-4xl mt-2 tracking-tight">{value}</p>
  </div>
);
